import { watch, onBeforeUnmount, nextTick, ref, type Ref, type ComputedRef } from "vue";
import type { Virtualizer } from "@tanstack/vue-virtual";

interface UseMessageScrollOptions {
  parentRef: Ref<HTMLElement | null>;
  rowVirtualizer: Ref<Virtualizer<HTMLElement, Element>>;
  virtualRows: ComputedRef<ReturnType<Virtualizer<HTMLElement, Element>["getVirtualItems"]>>;
  messages: ComputedRef<Array<{ id: string }>>;
  conversationKey: ComputedRef<string>;
  isLoading: ComputedRef<boolean>;
  isLoadingOlderMessages: ComputedRef<boolean>;
  hasMoreMessages: ComputedRef<boolean>;
  onLoadOlderMessages: () => void;
  /** A specific message to scroll to and briefly highlight once loaded, e.g. from clicking an Interaction History entry. */
  highlightMessageId?: ComputedRef<string | null | undefined>;
}

const HIGHLIGHT_DURATION_MS = 1500;
const MAX_HIGHLIGHT_PAGE_ATTEMPTS = 10;

const NEAR_BOTTOM_THRESHOLD = 150;
const MAX_CACHE_SIZE = 50;

// Module-level singleton: persists across component remounts for the SPA session
const scrollStateCache = new Map<string, { messageId: string | null }>();

export function useMessageScroll(options: UseMessageScrollOptions) {
  const {
    parentRef,
    rowVirtualizer,
    virtualRows,
    messages,
    conversationKey,
    isLoading,
    isLoadingOlderMessages,
    hasMoreMessages,
    onLoadOlderMessages,
    highlightMessageId,
  } = options;

  let hasScrolledToBottom = false;
  let isInitialMount = true;
  let anchorMessageId: string | null = null;
  let isRestoringScroll = false;
  const isScrollReady = ref(true);
  const highlightedMessageId = ref<string | null>(null);
  let highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastHighlightedTarget: string | null = null;
  let highlightPageAttempts = 0;

  // Scrolls to and briefly highlights a specific message. Returns false if the
  // message isn't in the currently loaded set (e.g. older than what's fetched).
  const scrollToAndHighlight = (messageId: string): boolean => {
    const index = messages.value.findIndex((m) => m.id === messageId);
    if (index === -1) return false;

    rowVirtualizer.value.scrollToIndex(index, { align: "center", behavior: "auto" });
    highlightedMessageId.value = messageId;
    if (highlightTimeoutId) clearTimeout(highlightTimeoutId);
    highlightTimeoutId = setTimeout(() => {
      highlightedMessageId.value = null;
    }, HIGHLIGHT_DURATION_MS);
    return true;
  };

  // If a highlight target is pending and hasn't been handled yet, scroll/highlight it
  // instead of the normal restore/scroll-to-bottom behavior. The target may be older
  // than the currently loaded page (only the most recent messages are fetched up front),
  // so when it's not found we page back via onLoadOlderMessages and keep retrying — up to
  // a bound — instead of silently giving up. Returns true when handled (highlighted, or a
  // page load was kicked off to keep looking) so callers should skip their normal fallback.
  const tryHandlePendingHighlight = (): boolean => {
    const targetId = highlightMessageId?.value;
    if (!targetId || targetId === lastHighlightedTarget) return false;

    if (scrollToAndHighlight(targetId)) {
      lastHighlightedTarget = targetId;
      highlightPageAttempts = 0;
      hasScrolledToBottom = true;
      isInitialMount = false;
      isRestoringScroll = false;
      return true;
    }

    if (hasMoreMessages.value && highlightPageAttempts < MAX_HIGHLIGHT_PAGE_ATTEMPTS) {
      highlightPageAttempts++;
      isRestoringScroll = true;
      onLoadOlderMessages();
      return true;
    }

    console.warn("Interaction History target message not found in conversation history", targetId);
    lastHighlightedTarget = targetId;
    highlightPageAttempts = 0;
    isRestoringScroll = false;
    return false;
  };

  const scrollToBottom = (behavior: "auto" | "smooth" = "auto") => {
    if (messages.value.length > 0 && parentRef.value) {
      rowVirtualizer.value.scrollToIndex(messages.value.length - 1, {
        align: "end",
        behavior,
      });
      hasScrolledToBottom = true;
      isInitialMount = false;
    }
  };

  const restoreScrollState = () => {
    const cached = scrollStateCache.get(conversationKey.value);
    if (cached && cached.messageId && parentRef.value) {
      const messageIndex = messages.value.findIndex((m) => m.id === cached.messageId);
      if (messageIndex !== -1) {
        rowVirtualizer.value.scrollToIndex(messageIndex, {
          align: "start",
          behavior: "auto",
        });
        hasScrolledToBottom = true;
        isInitialMount = false;
        return;
      }
    }
    scrollToBottom();
  };

  watch(
    [isLoading, () => messages.value.length],
    ([loading, len], [wasLoading]) => {
      if (wasLoading && !loading && len > 0 && !hasScrolledToBottom) {
        isScrollReady.value = false;
        nextTick(() =>
          requestAnimationFrame(() => {
            if (!tryHandlePendingHighlight()) restoreScrollState();
            isScrollReady.value = true;
          })
        );
        return;
      }
      if (isInitialMount && len > 0 && !hasScrolledToBottom && !loading) {
        isScrollReady.value = false;
        nextTick(() =>
          requestAnimationFrame(() => {
            if (!tryHandlePendingHighlight()) scrollToBottom();
            isScrollReady.value = true;
          })
        );
      }
    },
    { flush: "post", immediate: true }
  );

  // Handles a new highlight target arriving while the conversation is already loaded
  // and stable (e.g. the panel is kept alive via v-show and the user clicks another
  // Interaction History entry pointing into the same conversation) — none of the other
  // watchers above fire in that case since neither isLoading nor conversationKey change.
  if (highlightMessageId) {
    watch(
      highlightMessageId,
      (targetId) => {
        if (!targetId || targetId === lastHighlightedTarget || isLoading.value) return;
        nextTick(() => requestAnimationFrame(() => tryHandlePendingHighlight()));
      },
      { flush: "post" }
    );
  }

  watch(
    isLoadingOlderMessages,
    (loading, wasLoading) => {
      if (wasLoading && !loading && tryHandlePendingHighlight()) return;

      if (wasLoading && !loading && anchorMessageId) {
        isRestoringScroll = true;
        const anchorIndex = messages.value.findIndex((m) => m.id === anchorMessageId);
        if (anchorIndex !== -1) {
          rowVirtualizer.value.scrollToIndex(anchorIndex, {
            align: "start",
            behavior: "auto",
          });
        }
        anchorMessageId = null;
        requestAnimationFrame(() => {
          isRestoringScroll = false;
        });
      }
    },
    { flush: "post" }
  );

  watch(
    () => messages.value.length,
    (newLength, oldLength) => {
      if (newLength > oldLength && !isInitialMount && !isRestoringScroll) {
        nextTick(() => {
          requestAnimationFrame(() => {
            if (parentRef.value) {
              const { scrollTop, scrollHeight, clientHeight } = parentRef.value;
              const isNearBottom = scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_THRESHOLD;
              if (isNearBottom) {
                scrollToBottom("smooth");
              }
            }
          });
        });
      }
    },
    { flush: "post" }
  );

  watch(
    conversationKey,
    (newKey, oldKey) => {
      if (newKey !== oldKey) {
        isInitialMount = true;
        hasScrolledToBottom = false;
        anchorMessageId = null;
        isRestoringScroll = false;
        isScrollReady.value = false;

        if (messages.value.length > 0 && !isLoading.value) {
          nextTick(() =>
            requestAnimationFrame(() => {
              if (!tryHandlePendingHighlight()) restoreScrollState();
              isScrollReady.value = true;
            })
          );
        }
      }
    },
    { flush: "post" }
  );

  onBeforeUnmount(() => {
    if (highlightTimeoutId) clearTimeout(highlightTimeoutId);
    if (parentRef.value && messages.value.length > 0) {
      const firstVisible = virtualRows.value[0];
      const firstVisibleMessage = firstVisible ? messages.value[firstVisible.index] : null;

      scrollStateCache.set(conversationKey.value, {
        messageId: firstVisibleMessage?.id || null,
      });

      if (scrollStateCache.size > MAX_CACHE_SIZE) {
        const firstKey = scrollStateCache.keys().next().value;
        if (firstKey) scrollStateCache.delete(firstKey);
      }
    }
  });

  const handleScroll = () => {
    if (!parentRef.value) return;
    const { scrollTop } = parentRef.value;

    if (
      scrollTop < 100 &&
      hasMoreMessages.value &&
      !isLoadingOlderMessages.value &&
      !isInitialMount &&
      !isRestoringScroll &&
      messages.value.length > 0
    ) {
      const firstVisible = virtualRows.value[0];
      if (firstVisible && messages.value[firstVisible.index]) {
        anchorMessageId = messages.value[firstVisible.index].id;
      }
      onLoadOlderMessages();
    }
  };

  return {
    handleScroll,
    scrollToBottom,
    isScrollReady,
    highlightedMessageId,
  };
}
